import { useState, useMemo } from 'react';
import type { DraftState, Manager, TeamLiveData } from '../types';
import type { TeamValuation } from '../lib/valuation';
import { TEAMS_BY_ABBR, teamFullName } from '../constants/teams';
import {
  type AdvisorSettings,
  analyzeOpponents,
  getEffectiveTeamProjections,
  generateLLMScoutingReport,
} from '../lib/aiAdvisor';
import { TeamLogo } from './TeamLogo';

interface Props {
  state: DraftState;
  manager: Manager;
  liveData: Record<string, TeamLiveData>;
  valuations: Record<string, TeamValuation>;
  settings: AdvisorSettings;
  onClose: () => void;
  onSelectTeamForNomination?: (abbr: string) => void;
  onOpenSettings?: () => void;
}

export function AIWarRoomModal({
  state,
  manager,
  liveData,
  valuations,
  settings,
  onClose,
  onSelectTeamForNomination,
  onOpenSettings,
}: Props) {
  const [activeTab, setActiveTab] = useState<'board' | 'opponents' | 'dossier'>('board');
  const [selectedTeam, setSelectedTeam] = useState<string>(state.availableTeams[0] ?? 'KC');
  const [scoutingReport, setScoutingReport] = useState<string | null>(null);
  const [loadingReport, setLoadingReport] = useState(false);

  // Opponents breakdown
  const opponents = useMemo(() => analyzeOpponents(state, manager.id), [state, manager.id]);

  // Rob's Roster stats
  const rosterStats = useMemo(() => {
    const teams = manager.teams.map(abbr => getEffectiveTeamProjections(abbr, liveData));
    const totalProjWins = teams.reduce((acc, t) => acc + t.projectedWins, 0);
    const slotsLeft = Math.max(0, state.rosterCap - manager.teamsDrafted);
    const avgPerSlot = slotsLeft > 0 ? (manager.budget / slotsLeft) : 0;
    return {
      teams,
      totalProjWins,
      slotsLeft,
      avgPerSlot,
    };
  }, [manager, liveData, state.rosterCap]);

  // Available teams classified
  const categorizedTeams = useMemo(() => {
    return state.availableTeams.map(abbr => {
      const proj = getEffectiveTeamProjections(abbr, liveData);
      const val = valuations[abbr];
      const fairVal = val?.suggested ?? Math.round(proj.projectedWins * 1.2);

      let category: 'MUST_TARGET' | 'PRICE_ENFORCE' | 'BAIT_RIVALS' | 'SLEEPER' | 'STANDARD' = 'STANDARD';
      if (proj.tier <= 2 && fairVal <= 9) category = 'MUST_TARGET';
      else if (proj.tier <= 2 && fairVal >= 12) category = 'BAIT_RIVALS';
      else if (proj.tier >= 3 && proj.projectedWins >= 8.4 && fairVal <= 7) category = 'SLEEPER';
      else if (fairVal >= 8) category = 'PRICE_ENFORCE';

      return {
        abbr,
        meta: TEAMS_BY_ABBR[abbr],
        proj,
        fairVal,
        category,
      };
    }).sort((a, b) => b.fairVal - a.fairVal);
  }, [state.availableTeams, liveData, valuations]);

  const handleGenerateReport = async (team?: string) => {
    setLoadingReport(true);
    try {
      const rep = await generateLLMScoutingReport(
        {
          managerName: manager.name,
          teamAbbr: team,
          state,
          liveData,
          valuations,
        },
        settings,
      );
      setScoutingReport(rep);
      setActiveTab('dossier');
    } catch (e: any) {
      setScoutingReport(`Error generating report: ${e?.message ?? 'Unknown error'}`);
    } finally {
      setLoadingReport(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-3 md:p-6 z-50">
      <div className="bg-slate-900 border-2 border-cyan-500/80 rounded-2xl w-full max-w-5xl max-h-[92vh] flex flex-col shadow-2xl shadow-cyan-950/50 overflow-hidden">
        {/* Modal Header */}
        <div className="p-4 md:px-6 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-b border-cyan-500/30 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🧠</span>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-black text-white">
                  Rob's AI Commissioner War Room
                </h2>
                <span className="pill bg-cyan-950 text-cyan-300 border border-cyan-500/50 text-[10px] uppercase font-mono">
                  {settings.playstyle} Mode
                </span>
              </div>
              <p className="text-xs text-slate-400">
                Real-time game theory, opponent vulnerability matrix & valuation intelligence
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {onOpenSettings && (
              <button
                onClick={onOpenSettings}
                className="px-2.5 py-1 text-xs rounded bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700"
              >
                ⚙️ AI Settings
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1 px-2.5 rounded-lg bg-slate-800 hover:bg-rose-900 text-slate-400 hover:text-white transition text-sm font-bold"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Executive Summary Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 p-3 px-6 bg-slate-950/60 border-b border-white/5 text-xs">
          <div>
            <span className="text-slate-500 uppercase text-[10px] block font-bold">Your Budget</span>
            <span className="text-lg font-black text-emerald-400">${manager.budget}</span>
            <span className="text-slate-400 text-[11px] ml-1">({rosterStats.slotsLeft} slots left)</span>
          </div>
          <div>
            <span className="text-slate-500 uppercase text-[10px] block font-bold">Avg $ / Slot</span>
            <span className="text-lg font-black text-teal-300">${rosterStats.avgPerSlot.toFixed(1)}</span>
          </div>
          <div>
            <span className="text-slate-500 uppercase text-[10px] block font-bold">Projected Roster Wins</span>
            <span className="text-lg font-black text-cyan-300">{rosterStats.totalProjWins.toFixed(1)}</span>
            <span className="text-slate-400 text-[11px] ml-1">({manager.teams.length} teams)</span>
          </div>
          <div>
            <span className="text-slate-500 uppercase text-[10px] block font-bold">Opponent Status</span>
            <span className="text-amber-400 font-extrabold text-xs block truncate mt-1">
              {opponents.filter(o => o.isVulnerable).length} rivals cap-squeezed
            </span>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-slate-800 px-6 bg-slate-900/50">
          <button
            onClick={() => setActiveTab('board')}
            className={`py-3 px-4 text-xs font-bold border-b-2 transition ${
              activeTab === 'board'
                ? 'border-cyan-400 text-cyan-300'
                : 'border-transparent text-slate-400 hover:text-white'
            }`}
          >
            📋 Tactical Board Tier List ({categorizedTeams.length})
          </button>
          <button
            onClick={() => setActiveTab('opponents')}
            className={`py-3 px-4 text-xs font-bold border-b-2 transition ${
              activeTab === 'opponents'
                ? 'border-cyan-400 text-cyan-300'
                : 'border-transparent text-slate-400 hover:text-white'
            }`}
          >
            🎯 Opponent Threat Matrix ({opponents.length})
          </button>
          <button
            onClick={() => setActiveTab('dossier')}
            className={`py-3 px-4 text-xs font-bold border-b-2 transition ${
              activeTab === 'dossier'
                ? 'border-cyan-400 text-cyan-300'
                : 'border-transparent text-slate-400 hover:text-white'
            }`}
          >
            📑 AI Tactical Dossier
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
          {/* TAB 1: TACTICAL BOARD TIER LIST */}
          {activeTab === 'board' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-xs text-slate-400">
                  Every available team evaluated for Rob's auction advantage:
                </p>
                <button
                  onClick={() => handleGenerateReport()}
                  disabled={loadingReport}
                  className="px-3 py-1 text-xs rounded-lg bg-cyan-600 hover:bg-cyan-500 text-slate-950 font-bold"
                >
                  {loadingReport ? 'Analyzing...' : '⚡ Generate Full Draft Dossier'}
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {categorizedTeams.map(item => {
                  const badge = (() => {
                    switch (item.category) {
                      case 'MUST_TARGET':
                        return { label: '🎯 MUST TARGET', class: 'bg-emerald-600 text-white' };
                      case 'BAIT_RIVALS':
                        return { label: '🔥 BAIT RIVALS', class: 'bg-rose-600 text-white' };
                      case 'SLEEPER':
                        return { label: '💎 DEEP SLEEPER', class: 'bg-purple-600 text-white' };
                      case 'PRICE_ENFORCE':
                        return { label: '⚡ PRICE ENFORCE', class: 'bg-amber-600 text-slate-950' };
                      default:
                        return { label: 'FAIR VALUE', class: 'bg-slate-700 text-slate-300' };
                    }
                  })();

                  return (
                    <div
                      key={item.abbr}
                      className="bg-slate-800/60 rounded-xl p-3 border border-slate-700 flex flex-col justify-between hover:border-cyan-500/50 transition"
                    >
                      <div>
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2">
                            <TeamLogo abbr={item.abbr} size={32} />
                            <div>
                              <div className="font-extrabold text-sm text-white">{teamFullName(item.abbr)}</div>
                              <div className="text-[10px] text-slate-400">Tier {item.proj.tier} · Bye Wk {item.proj.byeWeek}</div>
                            </div>
                          </div>
                          <span className={`text-[9px] font-black px-2 py-0.5 rounded-full ${badge.class}`}>
                            {badge.label}
                          </span>
                        </div>

                        <div className="grid grid-cols-2 gap-1 text-[11px] mb-2 bg-black/20 p-2 rounded">
                          <div>
                            <span className="text-slate-400">Fair Value:</span>{' '}
                            <strong className="text-teal-300">${item.fairVal}</strong>
                          </div>
                          <div>
                            <span className="text-slate-400">Proj Wins:</span>{' '}
                            <strong className="text-white">{item.proj.projectedWins.toFixed(1)}W</strong>
                          </div>
                        </div>

                        <p className="text-[11px] text-slate-300 line-clamp-2 italic mb-3">
                          "{item.proj.notes}"
                        </p>
                      </div>

                      <div className="flex gap-2 pt-2 border-t border-white/5">
                        <button
                          onClick={() => {
                            setSelectedTeam(item.abbr);
                            handleGenerateReport(item.abbr);
                          }}
                          className="flex-1 py-1 text-xs rounded bg-slate-700 hover:bg-slate-600 text-slate-200"
                        >
                          Scout Dossier
                        </button>
                        {onSelectTeamForNomination && (
                          <button
                            onClick={() => {
                              onSelectTeamForNomination(item.abbr);
                              onClose();
                            }}
                            className="py-1 px-3 text-xs rounded bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold"
                          >
                            Nominate
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* TAB 2: OPPONENT THREAT & VULNERABILITY MATRIX */}
          {activeTab === 'opponents' && (
            <div className="space-y-4">
              <p className="text-xs text-slate-400">
                Real-time cap constraints and leverage analysis for all league rivals:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {opponents.map(opp => (
                  <div
                    key={opp.manager.id}
                    className={`rounded-xl p-4 border flex flex-col justify-between ${
                      opp.isVulnerable
                        ? 'bg-rose-950/20 border-rose-500/50'
                        : 'bg-slate-800/60 border-slate-700'
                    }`}
                  >
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">{opp.manager.emoji}</span>
                          <div>
                            <h3 className="text-base font-extrabold text-white">{opp.manager.name}</h3>
                            <span className="text-[10px] text-slate-400">{opp.manager.teamsDrafted} teams drafted</span>
                          </div>
                        </div>
                        <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${
                          opp.threatLevel === 'HIGH' ? 'bg-rose-600 text-white' : opp.threatLevel === 'MEDIUM' ? 'bg-amber-600 text-slate-950' : 'bg-emerald-600 text-white'
                        }`}>
                          {opp.threatLevel} THREAT
                        </span>
                      </div>

                      <div className="grid grid-cols-3 gap-2 text-center text-xs bg-black/30 p-2.5 rounded-lg mb-3">
                        <div>
                          <div className="text-[10px] uppercase text-slate-400">Budget</div>
                          <div className="font-extrabold text-emerald-400 text-sm">${opp.manager.budget}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase text-slate-400">Max Bid</div>
                          <div className="font-extrabold text-teal-300 text-sm">${opp.maxBid}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase text-slate-400">Slots Left</div>
                          <div className="font-extrabold text-white text-sm">{opp.slotsRemaining}</div>
                        </div>
                      </div>

                      <div className="text-xs text-slate-300 bg-slate-900/80 p-2.5 rounded border border-white/5 mb-3">
                        🎯 <strong>Vulnerability:</strong> {opp.vulnerabilityNote}
                      </div>

                      {opp.manager.teams.length > 0 && (
                        <div className="text-[11px] text-slate-400">
                          <strong>Drafted:</strong> {opp.manager.teams.join(', ')}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB 3: AI TACTICAL DOSSIER */}
          {activeTab === 'dossier' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-extrabold text-white">
                  Executive Scouting Dossier & Strategic Report
                </h3>
                <button
                  onClick={() => handleGenerateReport(selectedTeam)}
                  disabled={loadingReport}
                  className="px-3 py-1 text-xs rounded bg-cyan-600 hover:bg-cyan-500 text-slate-950 font-bold"
                >
                  {loadingReport ? 'Generating…' : '🔄 Refresh Dossier'}
                </button>
              </div>

              {loadingReport && (
                <div className="p-8 text-center bg-slate-800/40 rounded-xl border border-slate-700">
                  <div className="inline-block animate-spin text-2xl mb-2">⚡</div>
                  <p className="text-xs text-cyan-300 font-bold">Synthesizing draft model parameters & game-theory strategies…</p>
                </div>
              )}

              {!loadingReport && scoutingReport && (
                <div className="bg-slate-950/80 p-5 rounded-xl border border-cyan-500/30 text-xs text-slate-200 leading-relaxed font-sans whitespace-pre-wrap">
                  {scoutingReport}
                </div>
              )}

              {!loadingReport && !scoutingReport && (
                <div className="p-8 text-center bg-slate-800/40 rounded-xl border border-slate-700">
                  <p className="text-xs text-slate-400 mb-3">No scouting dossier loaded yet.</p>
                  <button
                    onClick={() => handleGenerateReport()}
                    className="px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold text-xs"
                  >
                    Generate Overall Draft Strategy Dossier
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
